import { Button, Flex, message, TablePaginationConfig } from "antd";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import { getColumns } from "./utils/table";
import { ExpenseUnit } from "@/types/expenseUnit";
import { DEFAULT_PAGINATION } from "@/constants/table";
import { useMemo, useState } from "react";
import useModal from "@/hooks/useModal";
import {
  useGetExpenseUnits,
  useGetExpenseUnitsOptions,
} from "../../hooks/useExpenseUnits";
import {
  useDeleteExpenseUnit,
  useRestoreExpenseUnit,
} from "../../hooks/useExpenseUnits";
import Table, { FilterOptions } from "@/components/table";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";

const ExpenseUnits = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<ExpenseUnit>();

  const { mutate: deleteExpenseUnit } = useDeleteExpenseUnit();
  const { mutate: restoreExpenseUnit } = useRestoreExpenseUnit();
  const {
    data: expenseUnits,
    isFetching,
    refetch,
  } = useGetExpenseUnits({
    params: {
      include_inactive: true,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseUnitsOptions({
    params: {
      only: "name,symbol,unit_type",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      name: (options || []).reduce((acc, item) => {
        if (item.name && !acc.some((option) => option.value === item?.name)) {
          acc.push({ text: item.name, value: item.name });
        }
        return acc;
      }, [] as FilterOptions[]),
      symbol: (options || []).reduce((acc, item) => {
        if (
          item.symbol &&
          !acc.some((option) => option.value === item?.symbol)
        ) {
          acc.push({ text: item.symbol, value: item.symbol });
        }
        return acc;
      }, [] as FilterOptions[]),
      unit_type: (options || []).reduce((acc, item) => {
        if (
          item.unit_type &&
          !acc.some((option) => option.value === item?.unit_type)
        ) {
          acc.push({ text: item.unit_type, value: item.unit_type });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        restoreExpenseUnit(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully activated expense unit");
            },
          }
        );
      } else {
        deleteExpenseUnit(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully deactivated expense unit");
            },
          }
        );
      }
    } catch (error) {
      throw error;
    }
  };

  // const columns = getColumns({
  //   onEdit: handleEdit,
  //   onToggleActive: handleToggleActive,
  // });

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end" align="center">
        <Button type="primary" onClick={openAddModal}>
          New Unit
        </Button>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onToggleActive: handleToggleActive,
            filterOptions,
          })
        }
        dataSource={expenseUnits?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            showSizeChanger: true,
          },
          dataPagination: expenseUnits?.pagination,
          onChange: setPagination,
        }}
        scroll={{
          x: "max-content",
        }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <CreateModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSuccess={() => refetch()}
      />
      <EditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSuccess={() => refetch()}
      />
    </Flex>
  );
};

export default ExpenseUnits;
